import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SW_PATH = "/push-sw.js";

function urlBase64ToUint8Array(b64) {
    const padding = "=".repeat((4 - (b64.length % 4)) % 4);
    const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
    const rawData = atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
}

/**
 * Hook for managing Web Push notifications for the chat thread.
 * The token is required so subscription is associated with the right actor on the server.
 */
export function usePushNotifications(token) {
    const [supported, setSupported] = useState(false);
    const [permission, setPermission] = useState(
        typeof Notification !== "undefined" ? Notification.permission : "default",
    );
    const [subscribed, setSubscribed] = useState(false);
    const [busy, setBusy] = useState(false);
    const subRef = useRef(null);

    useEffect(() => {
        const ok =
            typeof window !== "undefined" &&
            "serviceWorker" in navigator &&
            "PushManager" in window &&
            "Notification" in window;
        setSupported(ok);
        if (!ok) return;
        // Check existing subscription
        navigator.serviceWorker.getRegistration(SW_PATH).then(async (reg) => {
            if (!reg) return;
            const existing = await reg.pushManager.getSubscription();
            if (existing) {
                subRef.current = existing;
                setSubscribed(true);
            }
        });
    }, []);

    const subscribe = useCallback(async () => {
        if (!supported || !token) return false;
        setBusy(true);
        try {
            // Request permission if needed
            if (Notification.permission === "default") {
                const result = await Notification.requestPermission();
                setPermission(result);
                if (result !== "granted") return false;
            } else if (Notification.permission !== "granted") {
                setPermission(Notification.permission);
                return false;
            }

            // Register service worker
            const reg =
                (await navigator.serviceWorker.getRegistration(SW_PATH)) ||
                (await navigator.serviceWorker.register(SW_PATH));
            await navigator.serviceWorker.ready;

            // Get VAPID public key
            const { data: vapid } = await axios.get(`${API}/push/vapid-public-key`);
            if (!vapid?.public_key) return false;

            // Subscribe
            let sub = await reg.pushManager.getSubscription();
            if (!sub) {
                sub = await reg.pushManager.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey: urlBase64ToUint8Array(vapid.public_key),
                });
            }
            // Send to server
            const json = sub.toJSON();
            await axios.post(
                `${API}/push/subscribe`,
                {
                    endpoint: json.endpoint,
                    keys: json.keys,
                },
                { headers: { Authorization: `Bearer ${token}` } },
            );
            subRef.current = sub;
            setSubscribed(true);
            setPermission("granted");
            return true;
        } catch (e) {
            // eslint-disable-next-line no-console
            console.warn("Push subscribe failed", e);
            return false;
        } finally {
            setBusy(false);
        }
    }, [supported, token]);

    const unsubscribe = useCallback(async () => {
        if (!supported || !token) return false;
        setBusy(true);
        try {
            const reg = await navigator.serviceWorker.getRegistration(SW_PATH);
            if (!reg) return false;
            const sub = subRef.current || (await reg.pushManager.getSubscription());
            if (sub) {
                const endpoint = sub.endpoint;
                await sub.unsubscribe();
                try {
                    await axios.post(
                        `${API}/push/unsubscribe`,
                        { endpoint },
                        { headers: { Authorization: `Bearer ${token}` } },
                    );
                } catch {
                    /* ignore */
                }
            }
            subRef.current = null;
            setSubscribed(false);
            return true;
        } catch (e) {
            return false;
        } finally {
            setBusy(false);
        }
    }, [supported, token]);

    return { supported, permission, subscribed, busy, subscribe, unsubscribe };
}
